// PATH: src/components/catalog/GradePanel.tsx
import * as React from "react";
import { Box, Button, IconButton, Paper, Stack, Tooltip, Typography } from "@mui/material";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/EditOutlined";
import FlagIcon from "@mui/icons-material/FlagOutlined";
import { useGradesApi, useOfferingsApi, useTracksApi } from "../../api";
import type { CurriculumOffering, Subject, Track } from "../../models/domain";
import { tokens } from "../../theme/theme";
import { StatusPill } from "../ui";
import {
  cascadeLines,
  normalizeText,
  offeringAppliesToTrack,
  offeringTracks,
  offeringsOnlyForTrack,
  plural,
  sortTracks,
  subjectColor,
  type GradeEntry,
} from "./catalogModel";
import { useCatalog } from "./CatalogContext";
import { OfferingDialog } from "./forms/OfferingDialog";
import { SimpleEntityDialog } from "./forms/SimpleEntityDialog";
import { MissionZeroDialog } from "./MissionZeroDialog";
import { DetailHeader, Facts, ListRow, Rank, Section, SectionEmpty } from "./parts";
import { PlanetButton } from "./PlanetPreview";
import { SubjectDialog } from "./forms/SubjectDialog";
import { useCatalogDelete } from "./useCatalogDelete";
import { useDialogState } from "./useDialogState";

type DialogState =
  | { type: "grade" }
  | { type: "missionZero" }
  | { type: "track"; track: Track | null }
  | { type: "offering"; offering: CurriculumOffering | null }
  | { type: "subjectVisuals"; subject: Subject };

/** Petites étiquettes de filières (codes) ; nom complet au survol. */
const TrackTags: React.FC<{ tracks: Track[] }> = ({ tracks }) => (
  <Stack direction="row" flexWrap="wrap" useFlexGap gap={0.5} component="span">
    {tracks.map((t) => (
      <Box
        key={t.id}
        component="span"
        title={t.name}
        className="mono"
        sx={{
          px: 0.75,
          borderRadius: 1,
          border: `1px solid ${tokens.borderStrong}`,
          color: "text.secondary",
          fontSize: "0.6875rem !important",
          lineHeight: 1.6,
        }}
      >
        {t.code}
      </Box>
    ))}
  </Stack>
);

export const GradePanel: React.FC<{ entry: GradeEntry }> = ({ entry }) => {
  const { index, select } = useCatalog();
  const { updateGrade, deleteGrade } = useGradesApi();
  const { createTrack, updateTrack, deleteTrack } = useTracksApi();
  const { deleteOffering } = useOfferingsApi();
  const remove = useCatalogDelete();
  const dlg = useDialogState<DialogState>();
  const [query, setQuery] = React.useState("");

  const program = entry.program.program;
  const stage = entry.stage.stage;
  const grade = entry.grade.grade;
  const tracks = sortTracks(entry.grade.tracks);
  const offerings = entry.grade.offerings ?? [];
  const q = normalizeText(query);
  const shown = q
    ? offerings.filter((o) => normalizeText(o.subject?.name).includes(q) || normalizeText(o.subject?.code).includes(q))
    : offerings;
  const editingTrack = dlg.state?.type === "track" ? dlg.state.track : null;
  const editingOffering = dlg.state?.type === "offering" ? dlg.state.offering : null;

  const deleteTrackAsk = (t: Track) => {
    const opened = offeringsOnlyForTrack(entry.grade, t.id);
    const names = opened.map((o) => o.subject?.name ?? "?").join(", ");
    void remove({
      entity: "track",
      label: `la filière « ${t.name} »`,
      cascade: ["l'ossature prévue pour cette filière"],
      warning: opened.length
        ? opened.length > 1
          ? `${opened.length} matières (${names}) sont réservées à cette seule filière : elles deviendront ouvertes à toutes les filières du niveau.`
          : `La matière ${names} est réservée à cette seule filière : elle deviendra ouverte à toutes les filières du niveau.`
        : undefined,
      remove: () => deleteTrack(t.id),
    });
  };

  return (
    <Stack spacing={2.5}>
      <DetailHeader
        kind="Niveau"
        crumbs={[
          { label: program.name, onClick: () => select({ kind: "program", id: program.id }) },
          { label: stage.name, onClick: () => select({ kind: "stage", id: stage.id }) },
        ]}
        title={grade.name}
        code={grade.code}
        actions={
          <>
            <Button variant="outlined" startIcon={<FlagIcon />} onClick={() => dlg.show({ type: "missionZero" })}>
              Mission Zéro
            </Button>
            <Button variant="outlined" startIcon={<EditIcon />} onClick={() => dlg.show({ type: "grade" })}>
              Modifier
            </Button>
          </>
        }
        onDelete={() =>
          void remove({
            entity: "grade",
            label: `le niveau « ${grade.name} »`,
            cascade: cascadeLines(index, { kind: "grade", id: grade.id }),
            chapters: { program_id: program.id, grade_id: grade.id },
            typeToConfirm: grade.code,
            remove: () => deleteGrade(grade.id),
            onDone: () => select({ kind: "stage", id: stage.id }),
          })
        }
        deleteLabel="Supprimer le niveau"
      />

      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
        <Typography variant="subtitle1" sx={{ mb: 2 }}>
          Identité
        </Typography>
        <Facts
          items={[
            { label: "Code", value: <span className="mono">{grade.code}</span> },
            { label: "Rang", value: grade.sort_order },
            { label: "Étape", value: stage.name },
            { label: "Filières", value: tracks.length || "Aucune" },
            { label: "Matières offertes", value: offerings.length },
          ]}
        />
      </Paper>

      <Section
        title="Filières"
        description={
          tracks.length
            ? "Les parcours du niveau. Une matière peut être réservée à certaines filières."
            : "Ce niveau n'a pas de filière : toutes ses matières valent pour tous ses élèves."
        }
        count={tracks.length}
        addLabel="Ajouter une filière"
        onAdd={() => dlg.show({ type: "track", track: null })}
        flush
      >
        {tracks.length === 0 ? null : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "repeat(2, minmax(0, 1fr))", xl: "repeat(3, minmax(0, 1fr))" },
              gap: 1,
              p: 1.5,
            }}
          >
            {tracks.map((t) => {
              const n = offerings.filter((o) => offeringAppliesToTrack(o.offering, t.id)).length;
              return (
                <Stack
                  key={t.id}
                  direction="row"
                  spacing={1.25}
                  alignItems="center"
                  sx={{
                    pl: 1.5,
                    pr: 0.5,
                    py: 0.75,
                    borderRadius: 2,
                    border: `1px solid ${tokens.border}`,
                    opacity: t.is_active ? 1 : 0.65,
                    "&:hover": { bgcolor: "action.hover" },
                    "&:hover .row-actions": { opacity: 1 },
                  }}
                >
                  <Typography className="mono" sx={{ fontWeight: 700, color: "primary.dark", minWidth: 36 }}>
                    {t.code}
                  </Typography>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 550 }} noWrap title={t.name} dir="auto">
                      {t.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      rang {t.sort_order} · {plural(n, "matière")}
                      {t.is_active ? "" : " · inactive"}
                    </Typography>
                  </Box>
                  <Stack direction="row" className="row-actions" sx={{ opacity: { xs: 1, md: 0.35 }, transition: "opacity 120ms" }}>
                    <Tooltip title="Modifier">
                      <IconButton size="small" aria-label={`Modifier la filière ${t.code}`} onClick={() => dlg.show({ type: "track", track: t })}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Supprimer">
                      <IconButton
                        size="small"
                        aria-label={`Supprimer la filière ${t.code}`}
                        sx={{ "&:hover": { color: "error.main" } }}
                        onClick={() => deleteTrackAsk(t)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Stack>
              );
            })}
          </Box>
        )}
      </Section>

      <Section
        title="Matières offertes"
        description="Les matières proposées à ce niveau, dans l'ordre d'affichage."
        count={offerings.length}
        addLabel="Proposer une matière"
        onAdd={() => dlg.show({ type: "offering", offering: null })}
        filter={offerings.length > 10 ? { value: query, onChange: setQuery, placeholder: "Filtrer les matières" } : undefined}
        flush
      >
        {offerings.length === 0 ? (
          <SectionEmpty
            title="Aucune matière proposée"
            description={
              (entry.program.subjects ?? []).length
                ? "Proposez les matières du programme enseignées à ce niveau."
                : "Créez d'abord les matières du programme (fiche du programme)."
            }
          />
        ) : shown.length === 0 ? (
          <SectionEmpty title="Aucune matière ne correspond" />
        ) : (
          shown.map((o) => {
            const restricted = offeringTracks(o.offering.track_ids, tracks);
            return (
              <ListRow
                key={o.offering.id}
                lead={<Rank n={o.offering.sort_order} />}
                primary={
                  <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 1, maxWidth: "100%" }}>
                    {o.subject && (
                      <PlanetButton
                        look={o.subject}
                        fallbackColor={subjectColor(o.subject)}
                        label={o.subject.name}
                        size={20}
                        onClick={() => o.subject && dlg.show({ type: "subjectVisuals", subject: o.subject })}
                      />
                    )}
                    <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                      {o.subject?.name ?? "Matière introuvable"}
                    </Box>
                  </Box>
                }
                code={o.subject?.code}
                dimmed={o.subject?.is_active === false}
                meta={
                  o.subject?.is_active === false ? (
                    <StatusPill title="Matière fermée aux élèves (fiche du programme)">Fermée aux élèves</StatusPill>
                  ) : undefined
                }
                secondary={
                  tracks.length === 0 ? undefined : restricted.length === 0 ? (
                    "Toutes les filières"
                  ) : restricted.length === tracks.length ? (
                    <span title="Chaque filière est cochée : une filière ajoutée plus tard ne sera pas incluse.">
                      Les {tracks.length} filières actuelles, cochées une à une
                    </span>
                  ) : (
                    <TrackTags tracks={restricted} />
                  )
                }
                onEdit={() => dlg.show({ type: "offering", offering: o })}
                onDelete={() =>
                  void remove({
                    entity: "offering",
                    label: `l'offre « ${o.subject?.name ?? "?"} » en ${grade.code}`,
                    warning:
                      "La matière reste dans le programme et ses chapitres importés ne sont pas supprimés ; elle n'est simplement plus proposée à ce niveau.",
                    remove: () => deleteOffering(o.offering.id),
                  })
                }
              />
            );
          })
        )}
      </Section>

      <SubjectDialog
        open={dlg.isOpen("subjectVisuals")}
        onClose={dlg.close}
        program={program}
        siblings={entry.program.subjects ?? []}
        subject={dlg.state?.type === "subjectVisuals" ? dlg.state.subject : null}
        initialTab="visuals"
      />
      <SimpleEntityDialog
        open={dlg.isOpen("grade")}
        onClose={dlg.close}
        entity="grade"
        title="Modifier le niveau"
        context={`${program.name} › ${stage.name}`}
        initial={grade}
        siblings={(entry.stage.grades ?? []).map((g) => g.grade)}
        successMessage="Niveau enregistré."
        onSubmit={async (v) => {
          await updateGrade(grade.id, { stage_id: stage.id, code: v.code, name: v.name, sort_order: v.sort_order });
        }}
      />
      <SimpleEntityDialog
        open={dlg.isOpen("track")}
        onClose={dlg.close}
        entity="track"
        title={editingTrack ? "Modifier la filière" : "Nouvelle filière"}
        context={`Niveau : ${grade.name}`}
        initial={editingTrack}
        siblings={tracks}
        withActive
        namePlaceholder="Sciences mathématiques"
        codePlaceholder="SM"
        successMessage={editingTrack ? "Filière enregistrée." : "Filière créée."}
        onSubmit={async (v) => {
          const payload = { grade_id: grade.id, code: v.code, name: v.name, sort_order: v.sort_order, is_active: v.is_active };
          if (editingTrack) await updateTrack(editingTrack.id, payload);
          else await createTrack(payload);
        }}
      />
      <OfferingDialog
        open={dlg.isOpen("offering")}
        onClose={dlg.close}
        grade={entry.grade}
        subjects={entry.program.subjects ?? []}
        offering={editingOffering}
      />
      <MissionZeroDialog open={dlg.isOpen("missionZero")} grade={grade} onClose={dlg.close} />
    </Stack>
  );
};

export default GradePanel;
